import {
    asyncScheduler,
    BehaviorSubject,
    forkJoin,
    Observable,
    of as observableOf,
    Subscription,
    throwError as observableThrowError,
} from "rxjs"

import { HttpParams } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { Router } from "@angular/router"
import { catchError, filter, map, share, tap } from "rxjs/operators"
import { PremiumSectionTopic } from "../models/collection/premiumSectionTopic.model"
import { CollectionNode } from "../models/collectionNode.model"
import { CollectionResourceRequestParams } from "../models/collectionResourceRequestParams.model"
import { LearningObject } from "../models/learningObject.model"
import { redirectToError } from "../utils/error-handling"
import ResourceUtils from "../utils/resource-utils"
import { LmApiService } from "./lm-api.service"
import { UserService } from "./user.service"
import { Location } from "@angular/common"
import { PreviewService } from "./preview.service"
import { ResourceType } from "../models/constants"

@Injectable()
export class CollectionService {
    public collectionsChange: BehaviorSubject<CollectionNode> = new BehaviorSubject<CollectionNode>(
        new CollectionNode({})
    )

    public activeCollectionsChange: BehaviorSubject<CollectionNode> =
        new BehaviorSubject<CollectionNode>(new CollectionNode({}))

    public activeSubCollectionsChange: BehaviorSubject<CollectionNode> =
        new BehaviorSubject<CollectionNode>(new CollectionNode({}))

    public activePremiumCollectionChange: BehaviorSubject<CollectionNode> =
        new BehaviorSubject<CollectionNode>(new CollectionNode({}))

    public forkRequestInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

    public collectionResourceCount: number = 24
    private collections: CollectionNode = new CollectionNode({})
    private collectionsById: { [id: string]: CollectionNode }
    private collectionSlugList: string[] = [] // used to check if the requested collection is already loaded
    private activeCollection: CollectionNode = new CollectionNode({})
    private subscriptions: Subscription[] = []
    private requestedSlug = null
    public isLoadingCollections: boolean = true

    constructor(
        private lmApi: LmApiService,
        private userService: UserService,
        private router: Router,
        private location: Location,
        private previewService: PreviewService
    ) {}

    public cancelSubscriptions() {
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe())
    }

    public requestCollections(slug: string, reload?: boolean) {
        if (this.isCollectionLoaded(slug) && !reload) {
            this.collectionsChange.next(this.collections)
            this.activeCollection = this.findCollection(this.collections, slug)
            this.activeCollectionsChange.next(this.activeCollection)
            if (this.collections.isPremium) {
                this.resetCollections()
                return
            }
            this.loadResourcesIfNeededFor(this.activeCollection)
            this.activeCollection?.children.forEach((subcollection: CollectionNode) =>
                this.loadResourcesIfNeededFor(subcollection)
            )
            return
        }
        if (slug === this.requestedSlug && !reload) {
            // Collection is not loaded, but the request has already been made
            return
        }
        this.cancelSubscriptions()
        this.requestedSlug = slug
        this.collectionSlugList = []
        this.collectionsById = {}

        const requestCollections: Observable<CollectionNode> = this.lmApi
            .getWithCookiesInParams(`api/v2/collection/data/${slug}/`, [], {
                params: {
                    is_preview: this.previewService.previewOnCollection,
                },
            })
            .pipe(
                catchError((error, caught) => this.catch404ExpiredCollection(error, caught)),
                catchError((error) =>
                    redirectToError(
                        this.router,
                        this.location,
                        error.status,
                        error[0] ? error : null,
                        null,
                        {
                            errorStatus: error?.error?.status,
                            resourceType: ResourceType.COLLECTION,
                        }
                    )
                ),
                filter((data) => !!data),
                map((data: ArrayBuffer) => this.nodeFromObject(data)),
                share(),
                tap((data: CollectionNode) => {
                    if (data.redirectUrl) window.location.href = data.redirectUrl
                })
            )

        this.subscriptions.push(
            requestCollections.subscribe((collections: any) =>
                this.receivedCollections(collections, slug)
            )
        )

        const collectionResInfo: Observable<ArrayBuffer> = this.lmApi
            .getWithCookiesInParams(
                `api/v2/collection/res_info/${slug}/`,
                ["user_content_permission"],
                {
                    params: {
                        is_preview: this.previewService.previewOnCollection,
                    },
                }
            )
            .pipe(catchError((error) => observableThrowError(error)))

        this.forkRequestInProgress.next(true)
        this.subscriptions.push(
            forkJoin(requestCollections, collectionResInfo).subscribe(
                ([topCollection, collectionsInfo]) => {
                    // TODO: fix above any
                    this.updateCollectionInfo([collectionsInfo])
                    this.collectionsChange.next(this.collectionsChange.value)
                    this.activeCollectionsChange.next(this.activeCollectionsChange.value)
                    this.forkRequestInProgress.next(false)
                    if (this.collectionsChange.value.isPremium) this.resetCollections()
                    if (this.previewService.previewOnCollection)
                        this.previewService.collectionId = this.activeCollection.id
                }
            )
        )

        const params: CollectionResourceRequestParams = new CollectionResourceRequestParams()

        this.subscriptions.push(
            forkJoin(requestCollections, this.requestResourcesFor(slug, params)).subscribe(
                ([topCollection, resourcesResponse]) => {
                    this.isLoadingCollections = false
                    const collection: CollectionNode = this.findCollection(this.collections, slug)
                    this.setFirstResourcesFor(collection, resourcesResponse)
                }
            )
        )
    }

    public requestResourcesFor(slug, params: CollectionResourceRequestParams): Observable<any> {
        this.isLoadingCollections = true
        let httpParams: HttpParams = new HttpParams()

        if (params.start && params.count) {
            httpParams = httpParams.append("start", params.start.toString())
            httpParams = httpParams.append("count", params.count.toString())
        } else {
            httpParams = httpParams.append("start", "0")
            httpParams = httpParams.append("count", this.collectionResourceCount.toString())
        }

        httpParams = httpParams.append("student", this.userService.hasStudentExperience())
        httpParams = httpParams.append("is_preview", this.previewService.previewOnCollection)

        // the trailing slash is more important than it seems
        const collectionUrl: string = `api/v2/collection/resources/${slug}/`
        const preloadedEndpoints = this.userService.getPreloadedEndpoints()

        if (!params.start && !!preloadedEndpoints?.hasOwnProperty(collectionUrl)) {
            return observableOf(preloadedEndpoints[collectionUrl])
        } else {
            return this.lmApi
                .getWithCookiesInParams(collectionUrl, ["user_content_permission"], {
                    params: httpParams,
                })
                .pipe(catchError((error) => observableThrowError(error)))
        }
    }

    public loadResourceFor(collection, params: CollectionResourceRequestParams) {
        if (collection.isLoadingResources) {
            return
        }
        collection.isLoadingResources = true
        this.requestResourcesFor(collection.slug, params).subscribe((results: any) => {
            collection.resources = collection.resources || {}
            const pageKey: number = this.getPage(params.start, params.count)
            if (results && results.objects) {
                results.objects.forEach((resource) => {
                    collection.resources[pageKey] = collection.resources[pageKey] || []
                    collection.resources[pageKey].push(new LearningObject(resource))
                })
                collection.nextResourcesURI = results.meta.next_uri || null
                collection.isLoadingResources = false
            }
            this.isLoadingCollections = false
        })
    }

    public getPage(start: number, count: number): number {
        const page: number = start / count
        return start % count ? page + 1 : page
    }

    public incrementViewCount(slug: string) {
        if (!slug) {
            return
        }
        this.lmApi
            .get(`/collection/view-count/${slug}/`)
            .pipe(catchError((error) => observableThrowError(error)))
            .subscribe()
    }

    public incrementSubcollectionViewCount(subcollectionId: string, subcollectionType: string) {
        if (!subcollectionId) {
            return
        }
        this.lmApi
            .get(`/subcollection/view_count/${subcollectionType}/${subcollectionId}/`)
            .pipe(catchError((error) => observableThrowError(error)))
            .subscribe()
    }

    public static getAdminLink(id: number): string {
        return `/admin/cms/collection/${id}`
    }

    private loadResourcesIfNeededFor(collection: CollectionNode) {
        if (!collection) {
            return
        }
        if (!collection.firstResourcesRequested && !collection.isLoadingResources) {
            if (!collection.parents.length) {
                this.forkRequestInProgress.next(true)
            }
            const params = new CollectionResourceRequestParams()
            this.subscriptions.push(
                this.requestResourcesFor(collection.slug, params).subscribe((response) => {
                    this.isLoadingCollections = false
                    this.setFirstResourcesFor(collection, response)
                    if (!collection.parents.length) {
                        this.forkRequestInProgress.next(false)
                    }
                })
            )
        }
    }

    private setFirstResourcesFor(collection: CollectionNode, resourcesResponse) {
        if (!collection) {
            return
        }
        collection.firstResourcesRequested = true
        if (resourcesResponse && resourcesResponse.objects) {
            if (resourcesResponse.meta.total) {
                collection.nextResourcesURI = resourcesResponse.meta.next_uri || null
                collection.totalResources = resourcesResponse.meta.total || null
                collection.isLoadingResources = false
                collection.resources = this.generateResourcePages(
                    this.collectionResourceCount,
                    collection.totalResources
                )
                resourcesResponse.objects.map((resource) => {
                    collection.resources[0].push(new LearningObject(resource))
                })
            } else {
                // this will prevent the loader to go on forever on sub-collections
                collection.resources = [[]]
            }
        }
    }

    private receivedCollections(collections, slug) {
        this.activeCollection = this.findCollection(collections, slug)
        this.collections = collections
        this.collectionsChange.next(collections)
        this.activeCollectionsChange.next(this.activeCollection)
        if (collections.isPremium) {
            this.resetCollections()
        }
        this.activeCollection?.children.forEach((subcollection: CollectionNode) =>
            this.loadResourcesIfNeededFor(subcollection)
        )
    }

    private resetCollections() {
        this.collections = new CollectionNode({})
        this.activeCollection = this.collections
        this.collectionsChange.next(this.collections)
        this.activeCollectionsChange.next(this.activeCollection)
        this.collectionSlugList = []
        this.cancelSubscriptions()
        this.requestedSlug = ""
        this.collectionsById = {}
    }

    private nodeFromObject(obj, parents = []) {
        if (!!obj && Object?.keys(obj)?.length === 0 && !!obj?.children) {
            return null
        }
        const node = new CollectionNode({})
        const childrenData = obj.children
        delete obj.children
        node.assignToObject(obj)
        node.parents = parents
        this.collectionSlugList.push(node.slug)
        if (childrenData && childrenData.length > 0) {
            const newParents = parents.slice()
            newParents.push(node)
            node.children = childrenData.map((child) => this.nodeFromObject(child, newParents))
            if (node.children[0] === null) {
                node.children = []
            }
        }
        this.collectionsById[node.id] = node
        return node
    }

    private static addMediaTypes(source, destination) {
        Object.keys(source).forEach((mediaType, index) => {
            if (mediaType in destination) {
                destination[mediaType] += source[mediaType]
            } else {
                destination[mediaType] = source[mediaType]
            }
        })
    }

    private static addGrades(source: string[], destination: string[]) {
        for (const grade of source) {
            if (destination.indexOf(grade) === -1) {
                destination.push(grade)
            }
        }
    }

    private static addResourcesCountByLanguage(source, destination) {
        Object.keys(source).forEach((language) => {
            if (language in destination) {
                destination[language] += source[language]
            } else {
                destination[language] = source[language]
            }
        })
    }

    private updateCollectionInfo(collectionsInfo) {
        if (!collectionsInfo) {
            return { mediaTypes: {}, grades: [], resourcesByLanguage: {} }
        }

        const allMediaTypes: { [mediaType: string]: number } = {}
        const allGrades = []
        const allResourcesByLanguage: { [language: string]: number } = {}
        collectionsInfo.forEach((collectionInfo) => {
            const collection: CollectionNode = this.collectionsById[collectionInfo.id]
            const { mediaTypes, grades, resourcesByLanguage } = this.updateCollectionInfo(
                collectionInfo.children
            )
            CollectionService.addMediaTypes(collectionInfo.media_types, mediaTypes)
            CollectionService.addMediaTypes(mediaTypes, allMediaTypes)
            CollectionService.addGrades(collectionInfo.grades, grades)
            CollectionService.addGrades(grades, allGrades)
            CollectionService.addResourcesCountByLanguage(
                collectionInfo.resources_by_language,
                resourcesByLanguage
            )
            CollectionService.addResourcesCountByLanguage(
                resourcesByLanguage,
                allResourcesByLanguage
            )
            if (collection) {
                CollectionService.addMediaTypes(mediaTypes, collection.resourceMediaTypes)
                CollectionService.addGrades(grades, collection.resourceGrades)
                collection.resourceGradesDisplay = ResourceUtils.getGradesDisplay(
                    collection.resourceGrades
                )
                collection.poster_images = collectionInfo.poster_images
                CollectionService.addResourcesCountByLanguage(
                    resourcesByLanguage,
                    collection.resourcesByLanguage
                )
            } else {
                console.warn(`Collection ${collectionInfo.id} could not be found.`)
            }
        })
        return {
            mediaTypes: allMediaTypes,
            grades: allGrades,
            resourcesByLanguage: allResourcesByLanguage,
        }
    }

    private isCollectionLoaded(slug: string): boolean {
        return this.collectionSlugList.indexOf(slug) !== -1
    }

    private findCollection(collection: CollectionNode, slug: string): CollectionNode {
        if (collection.slug.toLowerCase() === slug.toLowerCase()) {
            return collection
        }
        for (let i: number = 0; i < collection.children.length; i++) {
            const collectionResult: CollectionNode = this.findCollection(
                collection.children[i],
                slug
            )
            if (collectionResult) {
                return collectionResult
            }
        }
        return null
    }

    private generateResourcePages(itemsPerPage: number, totalItems): any[] {
        const pages: any[] = [...Array(Math.ceil(totalItems / itemsPerPage))].fill([])
        return pages.map((page) => page.slice())
    }

    public getPremiumHomeData(premiumCollectionSlug) {
        let url: string = `/api/v2/collection/premium/${premiumCollectionSlug}/`
        this.lmApi
            .getWithCookiesInParams(url, [], {
                params: { is_preview: this.previewService.previewOnCollection },
            })
            .pipe(
                catchError((error, caught) => this.catch404ExpiredCollection(error, caught)),
                catchError((error) => redirectToError(this.router, this.location, error.status)),
                tap((response: any) => {
                    this.activePremiumCollectionChange.next(response)
                    this.previewService.collectionId = response.id
                })
            )
            .subscribe()
    }

    public getPremiumSubcollectionData(collectionSlug, sectionURL, subcollectionURL) {
        let url: string = `/api/v2/collection/premium/${collectionSlug}/${sectionURL}/${subcollectionURL}/`
        this.lmApi
            .getWithCookiesInParams(url, ["user_content_permission"], {
                params: { is_preview: this.previewService.previewOnSubCollection },
            })
            .pipe(
                tap(
                    (response: any) => this.activeSubCollectionsChange.next(response),
                    catchError((error) => observableThrowError(error))
                ),
                catchError((error) => observableThrowError(error))
            )
            .subscribe(
                () => {},
                (error) => redirectToError(this.router, this.location, error.status)
            )
    }

    public showMoreTopicResources(topic: PremiumSectionTopic) {
        topic.isLoadingResources = true
        if (!topic.allResourcesLoaded()) {
            this.lmApi
                .getWithCookiesInParams(topic.loadMoreLink)
                .pipe(catchError((error) => observableThrowError(error)))
                .subscribe(
                    (data: ArrayBuffer) => {
                        topic.addFromResponse(data)
                        topic.showMoreResources()
                        topic.isLoadingResources = false
                    },
                    (error) => {
                        topic.isLoadingResources = false
                    }
                )
        } else {
            topic.showMoreResources()
            topic.isLoadingResources = false
        }
    }

    catch404ExpiredCollection(error, caught) {
        if (
            error.error.status === "expired" ||
            error.error.status === "unpublished" ||
            error.error.status === ""
        ) {
            const adminLink = {
                url: `/admin/cms/collection/${error.error.id}/`,
                title: "Collection",
            }

            return redirectToError(this.router, this.location, error.status, null, null, {
                adminLink,
                errorStatus: error?.error?.status,
                resourceType: ResourceType.COLLECTION,
            })
        }
    }
}
